import _ from "lodash";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { Action, LegalEntity } from "../constants/interfaces";
import api from "../utils/api";

interface State {
  legalEntities: LegalEntity[];
  error: Error | null;
  loading: boolean;
}

export function getInitialState(): State {
  return {
    legalEntities: [],
    loading: false,
    error: null
  };
}

export const legalEntityTypes = {
  GET_LEGAL_ENTITIES_REQUEST: "GET_LEGAL_ENTITIES_REQUEST",
  GET_LEGAL_ENTITIES_RESPONSE: "GET_LEGAL_ENTITIES_RESPONSE",
  GET_LEGAL_ENTITY_REQUEST: "GET_LEGAL_ENTITY_REQUEST",
  GET_LEGAL_ENTITY_RESPONSE: "GET_LEGAL_ENTITY_RESPONSE",
  UPDATE_LEGAL_ENTITY_REQUEST: "UPDATE_LEGAL_ENTITY_REQUEST",
  UPDATE_LEGAL_ENTITY_RESPONSE: "UPDATE_LEGAL_ENTITY_RESPONSE"
};

export const legalEntityActions: any = {
  getLegalEntities: () => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({ type: legalEntityTypes.GET_LEGAL_ENTITIES_REQUEST });
    try {
      const { data: legalEntities } = await api.get(
        "/contact/getUserLegalEntities"
      );
      dispatch({
        type: legalEntityTypes.GET_LEGAL_ENTITIES_RESPONSE,
        payload: legalEntities,
        error: false
      });
    } catch (e) {
      dispatch({
        type: legalEntityTypes.GET_LEGAL_ENTITIES_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  },

  getLegalEntity: (id: string) => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({ type: legalEntityTypes.GET_LEGAL_ENTITY_REQUEST });
    try {
      const { data: legalEntity } = await api.get(
        `/contact/getUserLegalEntityDetails/${id}`
      );
      dispatch({
        type: legalEntityTypes.GET_LEGAL_ENTITY_RESPONSE,
        payload: legalEntity,
        error: false
      });
      return legalEntity;
    } catch (e) {
      dispatch({
        type: legalEntityTypes.GET_LEGAL_ENTITY_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  },

  updateLegalEntity: (
    legalEntity: LegalEntity,
    callback?: () => void
  ) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    dispatch({ type: legalEntityTypes.UPDATE_LEGAL_ENTITY_REQUEST });
    try {
      await api.put(
        `contact/updateUserLegalEntityDetails/${legalEntity.cmAcmCaseIdentifier}`,
        legalEntity
      );
      dispatch({
        type: legalEntityTypes.UPDATE_LEGAL_ENTITY_RESPONSE,
        payload: legalEntity,
        error: false
      });
      if (callback) callback();
    } catch (e) {
      dispatch({
        type: legalEntityTypes.UPDATE_LEGAL_ENTITY_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  }
};

export default function legalEntity(state = getInitialState(), action: Action) {
  const { type, payload, error } = action;

  switch (type) {
    case legalEntityTypes.GET_LEGAL_ENTITIES_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case legalEntityTypes.GET_LEGAL_ENTITIES_RESPONSE: {
      if (error) {
        return {
          ...state,
          loading: false,
          error: payload,
          legalEntities: state.legalEntities
        };
      }
      return { ...state, loading: false, error: null, legalEntities: payload };
    }

    case legalEntityTypes.GET_LEGAL_ENTITY_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case legalEntityTypes.GET_LEGAL_ENTITY_RESPONSE: {
      if (error) {
        //  In the case of the error, we don't modify the reducer state
        return {
          ...state,
          loading: false,
          error: payload,
          legalEntities: state.legalEntities
        };
      }
      // In the case of success, we merge the existing entity record with retrieved record
      const newLegalEntity: LegalEntity = payload;
      const legalEntities = mergeEntityWithState(
        newLegalEntity,
        state.legalEntities
      );
      return { ...state, loading: false, error: null, legalEntities };
    }

    case legalEntityTypes.UPDATE_LEGAL_ENTITY_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case legalEntityTypes.UPDATE_LEGAL_ENTITY_RESPONSE: {
      if (error) {
        return { ...state, loading: false, error: payload };
      }
      // In the case of success, we merge the existing entity record with retrieved record
      const newLegalEntity: LegalEntity = payload;
      const legalEntities = mergeEntityWithState(
        newLegalEntity,
        state.legalEntities
      );
      return { ...state, loading: false, error: null, legalEntities };
    }
  }
  return state;
}

export const mergeEntityWithState = (
  updatedEntity: LegalEntity,
  existingLegalEntities: LegalEntity[]
): LegalEntity[] => {
  const legalEntities = [...existingLegalEntities];
  const existingEntityIndex: number = _.findIndex(
    legalEntities,
    (entity: LegalEntity) =>
      entity.cmAcmCaseIdentifier === updatedEntity.cmAcmCaseIdentifier
  );
  const existingLegalEntity = legalEntities[existingEntityIndex];
  existingEntityIndex === -1
    ? // If the entity doesn't exist in state, add to reducer
      legalEntities.push(updatedEntity)
    : // If the entity already exists, merge new data
      (legalEntities[existingEntityIndex] = _.merge(
        {},
        existingLegalEntity,
        updatedEntity
      ));
  return legalEntities;
};
